import qs from 'qs'
import axios from 'axios'
import Cookies from 'js-cookie'
import { getToken, getUserRegion, sendException, fetchTokenRegionPromise, requestWithRetry, getAppDomain } from '@ssr/utils'

const SERVICE_DOMAIN_UNKNOWN = '{{SERVICE_DOMAIN_UNKNOWN}}'
const SERVICE_DOMAIN_CLOUD_UNKNOWN = '{{SERVICE_DOMAIN_CLOUD_UNKNOWN}}'

axios.interceptors.response.use(
  function (response) {
    const { status } = response
    if (status === 200) return response
    return Promise.reject(response)
  },
  function (err) {
    console.log('[Axios response error]', err)
    const { url, method, params, data, timeout } = err.config
    err.isRequestAborted = err.message === 'Request aborted'
    err.isTimeout = err.message === 'timeout of 0ms exceeded'
    if (url.startsWith(SERVICE_DOMAIN_UNKNOWN) || url.startsWith(SERVICE_DOMAIN_CLOUD_UNKNOWN)) {
      sendException(
        {
          name: 'TokenParseError',
          exception: err,
          message: `[${method.toUpperCase()}] ${url}, ${err.message}`,
          data: { token: getToken(), host: getHost(), userRegion: getUserRegion() },
        },
        {
          contexts: { AxiosFetch: { token: getToken(), host: getHost(), userRegion: getUserRegion() } },
          fingerprint: [url],
        }
      )
    } else {
      const requestId = err.response?.data?.requestId
      sendException(
        {
          name: 'AxiosError',
          exception: err,
          message: `[${method.toUpperCase()}] ${url}, ${err.message}, requestId: ${requestId}`,
          data: { params, data, requestId }
        },
        {
          contexts: { AxiosFetch: { method, url, params, data, timeout, msg: err?.response?.data?.error } },
          fingerprint: [err.name, err.message, url],
        }
      )
    }
    return Promise.reject(err)
  }
)

function getHost() {
  const userToken = getToken()
  if (userToken) {
    const userRegion = getUserRegion()
    if (!userRegion) {
      return SERVICE_DOMAIN_UNKNOWN
    }
    return userRegion === 1 ? window.SERVICE_DOMAIN_EXTERNAL_CN : window.SERVICE_DOMAIN_EXTERNAL_GLOBAL
  }
  if (location.pathname === '/cloud/benefits-contract') {
    // 对活动领取页特殊处理，未登录场景按照所在地区去请求对应的后端服务
    const country = Cookies.get('insta_app_country')
    return country === 'CN' ? window.SERVICE_DOMAIN_EXTERNAL_CN : window.SERVICE_DOMAIN_EXTERNAL_GLOBAL
  }
  if (getAppDomain()) {
    return getAppDomain() === 'c' ? window.SERVICE_DOMAIN_EXTERNAL_CN : window.SERVICE_DOMAIN_EXTERNAL_GLOBAL
  }
  return window.isCN ? window.SERVICE_DOMAIN_EXTERNAL_CN : window.SERVICE_DOMAIN_EXTERNAL_GLOBAL
}

function getCloudHost() {
  const userToken = getToken()
  if (userToken) {
    const userRegion = getUserRegion()
    if (!userRegion) {
      return SERVICE_DOMAIN_CLOUD_UNKNOWN
    }
    return userRegion === 1 ? window.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN : window.SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL
  }
  if (location.pathname === '/cloud/benefits-contract') {
    // 对活动领取页特殊处理，未登录场景按照所在地区去请求对应的后端服务
    const country = Cookies.get('insta_app_country')
    return country === 'CN' ? window.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN : window.SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL
  }
  return window.isCN ? window.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN : window.SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL
}

function proxyRequest(origin) {
  return async function () {
    const self = this
    await fetchTokenRegionPromise
      const args = Array.prototype.slice.call(arguments)
    const resolvedUrl = args[0]
      .replace(SERVICE_DOMAIN_UNKNOWN, getHost())
      .replace(SERVICE_DOMAIN_CLOUD_UNKNOWN, getCloudHost())
    args[0] = resolvedUrl

    if (args[2] && typeof args[2].beforeRequest === 'function') {
      const isCN =
        resolvedUrl.startsWith(window.SERVICE_DOMAIN_EXTERNAL_CN) ||
        resolvedUrl.startsWith(window.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN)
      const result = await args[2].beforeRequest({ isCN, isDev: !!window.isDev, payload: args[1] })
      if (result && 'payload' in result) {
        args[1] = result.payload
      }
      const { beforeRequest, ...restOpts } = args[2]
      args[2] = restOpts
    }

      return origin.apply(self, args)
  }
}

const proxyGet = requestWithRetry(proxyRequest(get))
const proxyPost = proxyRequest(post)
const proxyPut = proxyRequest(put)
const proxyPostStream = proxyRequest(postStream)

function get(url, query, config) {
  return new Promise((resolve, reject) => {
    axios
      .get(`${url}?${qs.stringify(query)}`, config)
      .then((res) => {
        if (res.status === 200) {
          resolve(res.data)
        } else {
          reject(res)
        }
      })
      .catch((err) => reject(err))
  })
}

function post(url, data, opts = {}) {
  if (opts.formData) {
    return new Promise((resolve, reject) => {
      axios
        .post(url, qs.stringify(data), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...(opts.headers || {}),
          },
          timeout: opts.timeout,
        })
        .then((res) => {
          if (res.status === 200) {
            resolve(res.data)
          }
        })
        .catch((err) => reject(err))
    })
  } else {
    return new Promise((resolve, reject) => {
      axios
        .post(url, data, { headers: opts.headers, timeout: opts.timeout })
        .then((res) => {
          if (res.status === 200) {
            resolve(res.data)
          }
        })
        .catch((err) => reject(err))
    })
  }
}

function postStream(url, data, opts = {}) {
  const headers = {
    'Content-Type': 'application/json',
    accept: 'text/event-stream, application/json, text/plain, */*',
    ...(getToken() ? { 'X-User-Token': getToken() } : {}),
    ...(opts.headers || {}),
  }
  // ===== 流式 =====
  if (typeof opts.onMessage === 'function') {
    return new Promise((resolve, reject) => {
      let lastIndex = 0
      let sseBuffer = ''            // 持久化 buffer
      let cancelSource
      const config = {
        method: 'post',
        url,
        data,
        headers,
        responseType: 'text',
        onDownloadProgress: (progressEvent) => {
          if (opts.signal && opts.signal.aborted) return
          const ev = progressEvent?.event || progressEvent
          const xhr = ev?.target || ev?.currentTarget
          const allText = xhr?.responseText || ''
          if (!allText) return
          const chunk = allText.substring(lastIndex)
          if (!chunk) return
          lastIndex = allText.length
          // 累加到持久 buffer
          sseBuffer += chunk
          let idx
          while ((idx = sseBuffer.indexOf('\n\n')) !== -1) {
            const part = sseBuffer.slice(0, idx)
            sseBuffer = sseBuffer.slice(idx + 2)

            const lines = part.split('\n')
            let eventType = 'message'
            const dataLines = []
            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventType = line.slice(6).trim()
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim())
              }
            }
            let dataStr = dataLines.join('\n')
            if (!dataStr) dataStr = part.trim()
            if (dataStr) {
              opts.onMessage({
                event: eventType,
                data: dataStr,
              })
            }
          }
        },
      }

      // Abort 支持
      if (opts.signal) {
        config.signal = opts.signal
        if (axios.CancelToken) {
          cancelSource = axios.CancelToken.source()
          config.cancelToken = cancelSource.token
          opts.signal.addEventListener('abort', () => {
            cancelSource?.cancel('aborted')
          })
        }
      }
      axios(config)
        .then(() => {
          // flush：处理没有 \n\n 结尾的最后一帧
          if (sseBuffer.trim()) {
            opts.onMessage({
              event: 'finish',
              data: sseBuffer.replace(/^data:/, '').trim(),
            })
            sseBuffer = ''
          }

          opts.onMessage('[DONE]')
          resolve()
        })
        .catch((err) => reject(err))
    })
  }
  // ===== 非流式 =====
  return axios
    .post(url, data, { headers, signal: opts?.signal })
    .then((res) => {
      if (res.status === 200) return res.data
      return Promise.reject(res)
    })
}

function put(url, data) {
  return new Promise((resolve, reject) => {
    axios
      .put(url, data)
      .then((res) => {
        if (res.status === 200) {
          resolve(res.data)
        } else {
          reject()
        }
      })
      .catch((err) => reject(err))
  })
}

/**
 * 公用获取Json字符串数据接口
 * @param {string} key Json的Key(必填)
 * @param {string} language 语言(选填)
 * @param {string} is_prod 是否强制使用生产环境数据(选填)
 */
function get_website_word(key, language, is_prod) {
  return new Promise((resolve, reject) => {
    let url = ApiBase.wwwService + `/website/word/getString`
    get(url, {
      'X-Language': language ? language : window.locale,
      key: key,
    }).then((res) => {
      if (res && res.code === 0 && res.data.word) {
        try {
          let data = JSON.parse(res.data.word.long_varchar)
          resolve(data)
        } catch (err) {
          console.log(`${key} 解析失败`)
          reject()
        }
      } else {
        console.log(`${key} 获取失败`)
        reject()
      }
    })
  })
}

function get_website_app_banner_word(local_id, language, is_prod) {
  return new Promise((resolve, reject) => {
    let url = ApiBase.wwwService + `/www/service/website/word/getBanner`
    get(url, {
      'X-Language': language ? language : window.locale,
      'X-App-Local-Id': local_id,
    }).then((res) => {
      if (res && res.code === 0 && res.data.banners) {
        try {
          let data = JSON.parse(res.data.banners.long_varchar)
          if (data.length > 0) {
            console.log('data', data, res.data?.banners?.id)
            data.forEach((item) => {
              item.ads_id = res.data?.banners?.id
            })
          }
          resolve(data)
        } catch (err) {
          console.log('benner 解析失败')
          reject()
        }
      } else {
        console.log('benner 获取失败')
        reject()
      }
    })
  })
}

function normalizeLanguage(str) {
  const arr = str.split('-')
  arr[1] = arr[1].toUpperCase()

  return arr.join('_')
}

function Base(path) {
  const host = getHost()
  return `${host}${path}`
}

function CloudBase(path) {
  const host = window.isDev ? getCloudHost() : getHost()
  return `${host}${path}`
}

const ApiBase = {
  get infrPointService() {
    return Base('/infr-point-service')
  },
  get diamondService() {
    return Base('/diamond-service')
  },
  get infrUserService() {
    return Base('/infr-user-service')
  },
  get communityService() {
    return Base('/community-service')
  },
  get wwwService() {
    return Base('/www-service')
  },
  get storeService() {
    return Base('/store-service')
  },
  get appService() {
    return Base('/app-service')
  },
  get infrBaseService() {
    return Base('/infr-base-service')
  },
  // 云存储 - 订阅
  get cloudSubscriptionService() {
    return CloudBase('/cloud-subscription-service')
  },
  get cloudService() {
    return CloudBase('/cloud/service')
  },
  get empty(){
    return Base('')
  }
}

export { proxyGet as get, proxyPost as post, proxyPut as put, get_website_word, get_website_app_banner_word, normalizeLanguage, ApiBase, proxyPostStream as postStream }
